export type ChatFontCategory =
  | 'sans'
  | 'rounded'
  | 'display'
  | 'tech'
  | 'handwritten'
  | 'serif'
  | 'mono'

export interface ChatFontFamilyInfo {
  readonly family: string
  readonly weights: readonly number[]
  readonly category: ChatFontCategory
  readonly wght?: readonly [number, number]
  readonly staffOnly?: true
}

export const CHAT_FONT_KEYS = [
  'DEFAULT',
  'INTER',
  'ROBOTO',
  'OPEN_SANS',
  'LATO',
  'MONTSERRAT',
  'POPPINS',
  'NUNITO',
  'WORK_SANS',
  'DM_SANS',
  'MANROPE',
  'FIGTREE',
  'LEXEND',
  'HELVETICA_LT_PRO',
  'HELVETICA_NEUE_LT_STD_43_LIGHT_EXTENDED',
  'HELVETICA_NEUE_LT_STD_53_EXTENDED',
  'HELVETICA_NEUE_LT_STD_63_MEDIUM_EXTENDED',
  'QUICKSAND',
  'COMFORTAA',
  'FREDOKA',
  'BALOO_2',
  'CHEWY',
  'VARELA_ROUND',
  'BEBAS_NEUE',
  'ANTON',
  'OSWALD',
  'RUSSO_ONE',
  'RIGHTEOUS',
  'ARCHIVO_BLACK',
  'BANGERS',
  'LUCKIEST_GUY',
  'DOTO',
  'BITCOUNT_GRID_SINGLE',
  'PRESS_START_2P',
  'VT323',
  'ORBITRON',
  'AUDIOWIDE',
  'SILKSCREEN',
  'CAVEAT',
  'PERMANENT_MARKER',
  'SHADOWS_INTO_LIGHT',
  'PATRICK_HAND',
  'PLAYFAIR_DISPLAY',
  'MERRIWEATHER',
  'LORA',
  'ITC_GARAMOND_STD_LIGHT',
  'JETBRAINS_MONO',
  'SPACE_MONO',
  'VCR_OSD_MONO'
] as const

export type ChatFontKey = typeof CHAT_FONT_KEYS[number]

export const CHAT_FONT_FAMILIES: Readonly<Record<Exclude<ChatFontKey, 'DEFAULT'>, ChatFontFamilyInfo>> = {
  INTER: { family: 'Inter', weights: [400, 700], category: 'sans', wght: [100, 900] },
  ROBOTO: { family: 'Roboto', weights: [400, 700], category: 'sans', wght: [100, 900] },
  OPEN_SANS: { family: 'Open Sans', weights: [400, 700], category: 'sans', wght: [300, 800] },
  LATO: { family: 'Lato', weights: [400, 700], category: 'sans' },
  MONTSERRAT: { family: 'Montserrat', weights: [400, 700], category: 'sans', wght: [100, 900] },
  POPPINS: { family: 'Poppins', weights: [400, 700], category: 'sans' },
  NUNITO: { family: 'Nunito', weights: [400, 700], category: 'sans', wght: [200, 1000] },
  WORK_SANS: { family: 'Work Sans', weights: [400, 700], category: 'sans', wght: [100, 900] },
  DM_SANS: { family: 'DM Sans', weights: [400, 700], category: 'sans', wght: [100, 1000] },
  MANROPE: { family: 'Manrope', weights: [400, 700], category: 'sans', wght: [200, 800] },
  FIGTREE: { family: 'Figtree', weights: [400, 700], category: 'sans', wght: [300, 900] },
  LEXEND: { family: 'Lexend', weights: [400, 700], category: 'sans', wght: [100, 900] },
  HELVETICA_LT_PRO: { family: 'Helvetica LT Pro', weights: [300, 400, 700, 900], category: 'sans', staffOnly: true },
  HELVETICA_NEUE_LT_STD_43_LIGHT_EXTENDED: { family: 'Helvetica Neue LT Std 43 Light Extended', weights: [400], category: 'sans', staffOnly: true },
  HELVETICA_NEUE_LT_STD_53_EXTENDED: { family: 'Helvetica Neue LT Std 53 Extended', weights: [400], category: 'sans', staffOnly: true },
  HELVETICA_NEUE_LT_STD_63_MEDIUM_EXTENDED: { family: 'Helvetica Neue LT Std 63 Medium Extended', weights: [400], category: 'sans', staffOnly: true },
  QUICKSAND: { family: 'Quicksand', weights: [400, 700], category: 'rounded', wght: [300, 700] },
  COMFORTAA: { family: 'Comfortaa', weights: [400, 700], category: 'rounded', wght: [400, 700] },
  FREDOKA: { family: 'Fredoka', weights: [400, 700], category: 'rounded', wght: [300, 700] },
  BALOO_2: { family: 'Baloo 2', weights: [400, 700], category: 'rounded', wght: [400, 800] },
  CHEWY: { family: 'Chewy', weights: [400], category: 'rounded' },
  VARELA_ROUND: { family: 'Varela Round', weights: [400], category: 'rounded' },
  BEBAS_NEUE: { family: 'Bebas Neue', weights: [400], category: 'display' },
  ANTON: { family: 'Anton', weights: [400], category: 'display' },
  OSWALD: { family: 'Oswald', weights: [400, 700], category: 'display', wght: [400, 700] },
  RUSSO_ONE: { family: 'Russo One', weights: [400], category: 'display' },
  RIGHTEOUS: { family: 'Righteous', weights: [400], category: 'display' },
  ARCHIVO_BLACK: { family: 'Archivo Black', weights: [400], category: 'display' },
  BANGERS: { family: 'Bangers', weights: [400], category: 'display' },
  LUCKIEST_GUY: { family: 'Luckiest Guy', weights: [400], category: 'display' },
  DOTO: { family: 'Doto', weights: [400, 700], category: 'display', wght: [100, 900] },
  BITCOUNT_GRID_SINGLE: { family: 'Bitcount Grid Single', weights: [400], category: 'display' },
  PRESS_START_2P: { family: 'Press Start 2P', weights: [400], category: 'tech' },
  VT323: { family: 'VT323', weights: [400], category: 'tech' },
  ORBITRON: { family: 'Orbitron', weights: [400, 700], category: 'tech', wght: [400, 900] },
  AUDIOWIDE: { family: 'Audiowide', weights: [400], category: 'tech' },
  SILKSCREEN: { family: 'Silkscreen', weights: [400, 700], category: 'tech' },
  CAVEAT: { family: 'Caveat', weights: [400, 700], category: 'handwritten', wght: [400, 700] },
  PERMANENT_MARKER: { family: 'Permanent Marker', weights: [400], category: 'handwritten' },
  SHADOWS_INTO_LIGHT: { family: 'Shadows Into Light', weights: [400], category: 'handwritten' },
  PATRICK_HAND: { family: 'Patrick Hand', weights: [400], category: 'handwritten' },
  PLAYFAIR_DISPLAY: { family: 'Playfair Display', weights: [400, 700], category: 'serif', wght: [400, 900] },
  MERRIWEATHER: { family: 'Merriweather', weights: [400, 700], category: 'serif', wght: [300, 900] },
  LORA: { family: 'Lora', weights: [400, 700], category: 'serif', wght: [400, 700] },
  ITC_GARAMOND_STD_LIGHT: { family: 'ITC Garamond Std Light', weights: [400], category: 'serif', staffOnly: true },
  JETBRAINS_MONO: { family: 'JetBrains Mono', weights: [400, 700], category: 'mono', wght: [400, 800] },
  SPACE_MONO: { family: 'Space Mono', weights: [400, 700], category: 'mono' },
  VCR_OSD_MONO: { family: 'VCR OSD Mono', weights: [400], category: 'mono', staffOnly: true }
}

export const CHAT_FONT_CATEGORY_ORDER: readonly ChatFontCategory[] = [
  'sans',
  'rounded',
  'display',
  'tech',
  'handwritten',
  'serif',
  'mono'
]
